import React, { ComponentProps, useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages } from 'react-intl';
import { useIntl } from 'react-intl';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import {
  BodyText,
  Button,
  Carousel,
  CarouselDots,
  Container,
  ContentfulImageTransformOptions,
  DisplayText,
  FallbackImage,
  IconLink,
  theme,
  useOpenURL,
  useResponsive,
  useToggleState,
} from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { HomeBannerContent } from '../../hooks';

const HomeBannerFallbackImage = require('./home-banner-fallback.jpg');

// ─────────────────────────────────────────────────────────────────────────────

export const HomeBanner = (props: HomeBannerProps) => {
  const { content, size } = props;

  const { formatMessage } = useIntl();

  // ─── Carousel ──────────────────────────────────────────────────────────────

  const {
    value: isInteractingWithCarousel,
    toggleOn: startCarouselInteraction,
    toggleOff: endCarouselInteraction,
  } = useToggleState(false);

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const carouselRef = useRef<CarouselRef>(undefined);

  const goToSlide = useCallback((slideIndex: number) => {
    carouselRef.current?.goToSlide(slideIndex);
  }, []);
  const goToPreviousSlide = useCallback(() => {
    carouselRef.current?.goToPrevSlide();
  }, []);
  const goToNextSlide = useCallback(() => {
    carouselRef.current?.goToNextSlide();
  }, []);

  // reset carousel index if content changes.
  useEffect(() => {
    carouselRef.current?.goToSlide(0);
    setActiveSlideIndex(0);
  }, [content]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Carousel
      hideDots
      carouselRef={carouselRef}
      navControlAccessibilityLabel={formatMessage(messages.homeBannerAccessibilityLabel)}
      onActiveSlideChange={setActiveSlideIndex}
      onBegin={startCarouselInteraction}
      onFinalize={endCarouselInteraction}
    >
      {content.map((item, index) => (
        <Content
          {...item}
          key={item.id}
          size={size}
          isInteractingWithCarousel={isInteractingWithCarousel}
          currentSlideIndex={index}
          activeSlideIndex={activeSlideIndex}
          goToSlide={goToSlide}
          goToPreviousSlide={goToPreviousSlide}
          goToNextSlide={goToNextSlide}
          totalSlides={content.length}
        />
      ))}
    </Carousel>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const Content = (props: ContentProps) => {
  const {
    id,
    title,
    description,
    cta,
    image,
    imageA11y,
    deepLink,
    foregroundColor = theme.colors.WHITE,
    size,
    currentSlideIndex,
    activeSlideIndex,
    totalSlides,
    goToSlide,
    goToPreviousSlide,
    goToNextSlide,
    isInteractingWithCarousel,
  } = props;

  // ───────────────────────────────────────────────────────────────────────────

  const openUrl = useOpenURL();
  const { track } = useTelemetry();

  const { formatMessage } = useIntl();
  const { match, currentBreakpoint } = useResponsive();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyle = match([
    [size === 'small' ? styles.smallerHeight : styles.largerHeight],
    [styles.largerViewportHeight],
  ]);

  const contentStyle = match([
    styles.contentXS,
    [styles.contentSM, size === 'small' ? styles.contentSMSmall : styles.contentSMLarge],
  ]);

  const textStyle = useStyle(() => ({ color: foregroundColor }), [foregroundColor]);

  const ctaStyle = match([styles.ctaXS, size === 'small' ? styles.ctaSMSmall : styles.ctaSMLarge]);

  const contentfulOptions = match([
    size === 'small' ? CONTENTFUL_OPTIONS_XS_SMALL : CONTENTFUL_OPTIONS_XS_LARGE,
    CONTENTFUL_OPTIONS_SM,
  ]);

  const canGoToNextSlide = currentSlideIndex < totalSlides - 1;
  const canGoToPrevSlide = currentSlideIndex > 0;

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const handleCtaPress = useCallback(() => {
    if (!deepLink) return;

    track('home.banner.cta_tapped', { id, destination: deepLink });

    openUrl(deepLink);
  }, [deepLink, id, openUrl, track]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyle}>
      <FallbackImage
        style={styles.image}
        aria-label={imageA11y}
        baseUrl={image}
        defaultImage={HomeBannerFallbackImage}
        contentfulOptions={contentfulOptions}
      />

      {deepLink && !cta ? (
        <TouchableOpacity
          aria-label={formatMessage(messages.homeBannerLinkAccessibilityLabel, { link: deepLink })}
          accessibilityRole="link"
          disabled={Platform.OS === 'web' && isInteractingWithCarousel}
          style={styles.pressableLink}
          onPress={handleCtaPress}
        />
      ) : null}

      <Container
        pointerEvents="box-none"
        removeSpacing={currentBreakpoint.isXS}
        style={styles.contentInnerWrapper}
        wrapperStyle={styles.contentWrapper}
      >
        <View style={contentStyle}>
          {title ? (
            <DisplayText bold sizeMatch={['24', '56']} style={textStyle}>
              {title}
            </DisplayText>
          ) : null}

          {description ? (
            <BodyText sizeMatch={['14', '24']} style={textStyle}>
              {description}
            </BodyText>
          ) : null}

          {totalSlides > 1 && currentBreakpoint.isXS ? (
            <CarouselDots
              layout="standard"
              colors={getCarouselDotsColors(foregroundColor)}
              style={styles.carouselDots}
              slidesCount={totalSlides}
              activeSlideIndex={activeSlideIndex}
              accessibilityLabel={formatMessage(messages.carouselDotsAccessibilityLabel)}
              onPress={goToSlide}
            />
          ) : null}

          {cta && deepLink ? (
            <Button
              palette="oatmeal"
              aria-label={cta}
              size={match(['medium', 'large'])}
              style={ctaStyle}
              onPress={handleCtaPress}
            >
              {cta}
            </Button>
          ) : null}
        </View>

        {totalSlides > 1 && !currentBreakpoint.isXS ? (
          <View style={styles.carouselDotsWrapper}>
            <CarouselDots
              layout="standard"
              colors={getCarouselDotsColors(foregroundColor)}
              style={styles.carouselDots}
              slidesCount={totalSlides}
              activeSlideIndex={activeSlideIndex}
              accessibilityLabel={formatMessage(messages.carouselDotsAccessibilityLabel)}
              onPress={goToSlide}
            />

            <IconLink
              name="IconArrowLeft"
              iconSize={24}
              disabled={!canGoToPrevSlide}
              accessibilityLabel={formatMessage(messages.previousSlide)}
              style={[styles.carouselArrow, !canGoToPrevSlide && styles.carouselArrowDisabled]}
              onPress={goToPreviousSlide}
            />

            <IconLink
              name="IconArrowRight"
              iconSize={24}
              disabled={!canGoToNextSlide}
              accessibilityLabel={formatMessage(messages.nextSlide)}
              style={[styles.carouselArrow, !canGoToNextSlide && styles.carouselArrowDisabled]}
              onPress={goToNextSlide}
            />
          </View>
        ) : null}
      </Container>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_HEIGHT_XS_SM = 160;
const IMAGE_HEIGHT_XS_LG = 393;
const IMAGE_HEIGHT_SM = 850;

const SHARED_CONTENTFUL_OPTIONS: Partial<ContentfulImageTransformOptions> = {
  fm: 'webp',
  fit: 'fill',
  q: 70,
};

const CONTENTFUL_OPTIONS_XS_SMALL: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  h: IMAGE_HEIGHT_XS_SM,
};

const CONTENTFUL_OPTIONS_XS_LARGE: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  h: IMAGE_HEIGHT_XS_LG,
};

const CONTENTFUL_OPTIONS_SM: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  h: IMAGE_HEIGHT_SM,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCarouselDotsColors(color: string) {
  return {
    inactive: {
      idle: color,
      hover: color,
      opacity: 0.3,
    },
    active: {
      idle: color,
      hover: color,
    },
  };
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  image: {
    ...StyleSheet.absoluteFill,
  },
  pressableLink: {
    ...StyleSheet.absoluteFill,
  },
  carouselDots: {
    marginVertical: 0, // overrides default dots vertical margin of 16px
    justifyContent: 'flex-start',
  },
  carouselDotsWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['6'],
  },
  carouselArrow: {
    width: 40,
    height: 40,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.WHITE,
  },
  carouselArrowDisabled: {
    opacity: 0.3,
  },
  smallerHeight: {
    minHeight: IMAGE_HEIGHT_XS_SM,
  },
  largerHeight: {
    minHeight: IMAGE_HEIGHT_XS_LG,
  },
  largerViewportHeight: {
    minHeight: IMAGE_HEIGHT_SM,
  },
  contentSMSmall: {
    alignItems: 'center',
  },
  contentSMLarge: {
    width: '45%',
    alignSelf: 'flex-start',
  },
  contentWrapper: {
    height: '100%',
    justifyContent: 'flex-end',
  },
  contentInnerWrapper: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing['6'],
    gap: theme.spacing['4'],
  },
  contentXS: {
    paddingHorizontal: theme.spacing['4'],
    gap: theme.spacing['4'],
  },
  contentSM: {
    gap: theme.spacing['6'],
  },
  ctaXS: {
    alignSelf: 'flex-start',
  },
  ctaSMSmall: {
    alignSelf: 'center',
    minWidth: 150,
    minHeight: 64,
  },
  ctaSMLarge: {
    alignSelf: 'flex-start',
    minWidth: 150,
    minHeight: 64,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  homeBannerAccessibilityLabel: {
    defaultMessage: 'Home Banner',
    description: 'Home | Home Banner | Accessibility Label',
  },
  homeBannerLinkAccessibilityLabel: {
    defaultMessage: 'Open {link}',
    description: 'Home | Home Banner Link | Accessibility Label',
  },
  carouselDotsAccessibilityLabel: {
    defaultMessage: 'Home Banner Carousel Dots',
    description: 'Home | Home Banner | Carousel Dots | Accessibility Label',
  },
  previousSlide: {
    defaultMessage: 'Previous Slide',
    description: 'Home | Home Banner | Carousel | Previous Slide | Accessibility Label',
  },
  nextSlide: {
    defaultMessage: 'Next Slide',
    description: 'Home | Home Banner | Carousel | Next Slide | Accessibility Label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ContentSize = 'small' | 'large';

type HomeBannerProps = {
  content: HomeBannerContent[];
  size: ContentSize;
};

type ContentProps = HomeBannerContent & {
  isInteractingWithCarousel: boolean;
  currentSlideIndex: number;
  activeSlideIndex: number;
  totalSlides: number;
  size: ContentSize;
  goToSlide: (slideIndex: number) => void;
  goToPreviousSlide: () => void;
  goToNextSlide: () => void;
};

type CarouselRef = NonNullable<ComponentProps<typeof Carousel>['carouselRef']>['current'];
